'use client'

import Link from 'next/link'
import { Menu, X } from 'lucide-react'
import React from 'react'
import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import { ThemeSwitcher } from '../../components/widgets/ThemeSwitcher'
import LanguageSwitcher from '../../components/widgets/LanguageSwitcher'
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@/components/ui/navigation-menu'

const menuItems = [
    { id: 'about', translationKey: 'navigation_about', href: '#about' },
    { id: 'topics', translationKey: 'navigation_topics', href: '#topics' },
    { id: 'appearances', translationKey: 'navigation_appearances', href: '#appearances' },
    { id: 'references', translationKey: 'navigation_references', href: '#testimonials' },
    { id: 'faq', translationKey: 'navigation_faq', href: '#faq' },
    { id: 'book_now', translationKey: 'navigation_book_now', href: '#book_now' },
]

export { menuItems }

interface HeaderProps {
    locale: string;
}

export const Header = ({ locale }: HeaderProps) => {
    const [menuState, setMenuState] = React.useState(false)
    const t = useTranslations('Navigation')
    const pathname = usePathname()

    return (
        <header role="banner">
            <nav
                className={`bg-background/85 fixed z-20 w-full border-b backdrop-blur-xl ${
                    menuState ? 'data-[state=active]' : ''
                }`}
                aria-label={t('mainNavigation')}>
                <div className="w-full transition-all duration-300">
                    <div className="h-16 flex items-center justify-between px-4 lg:px-8">
                        <div className="flex-none mr-4">
                            <Link
                                href="/"
                                className="font-bold text-2xl uppercase text-gradient-brand"
                                aria-label={t('home')}
                            >
                                {t('brandName')}
                            </Link>
                        </div>

                        <div className="flex-1 flex justify-center hidden lg:flex">
                            <NavigationMenu>
                                <NavigationMenuList className="flex items-center lg:gap-4 2xl:gap-4">
                                    {menuItems.map((item) => (
                                        <NavigationMenuItem key={item.id}>
                                            <NavigationMenuLink asChild active={pathname.includes(item.href.substring(1))}>
                                                <Link href={item.href} className={`font-bold lg:font-extrabold text-lg md:text-lg xl:text-2xl uppercase hover:text-primary transition-all duration-300 ease-in-out hover:scale-[1.02] hover:bg-transparent focus:bg-transparent focus:outline-none focus-visible:ring-0 text-foreground}`}>
                                                    {t(item.translationKey)}
                                                </Link>
                                            </NavigationMenuLink>
                                        </NavigationMenuItem>
                                    ))}
                                </NavigationMenuList>
                            </NavigationMenu>
                        </div>

                        <div className="flex items-center gap-2 justify-end ml-auto">
                            <ThemeSwitcher />
                            <LanguageSwitcher locale={locale} />
                            <button
                                onClick={() => setMenuState(!menuState)}
                                aria-label={menuState ? t('menuClose') : t('menuOpen')}
                                aria-expanded={menuState}
                                aria-controls="mobile-menu"
                                className="relative z-50 inline-flex items-center justify-center p-1 lg:hidden">
                                <Menu 
                                    className={`m-auto size-8 duration-200 ${
                                        menuState ? 'rotate-180 scale-0 opacity-0' : ''
                                    }`}
                                    aria-hidden="true"
                                />
                                <X 
                                    className={`absolute inset-0 m-auto size-8 duration-200 ${
                                        menuState ? 'rotate-0 scale-100 opacity-100' : '-rotate-180 scale-0 opacity-0'
                                    }`}
                                    aria-hidden="true"
                                />
                            </button>
                        </div>

                    </div>
                </div>
                <div 
                    id="mobile-menu"
                    className={`fixed top-[4rem] left-0 right-0 bg-background/95 w-full flex items-center justify-center  border-x border-b p-6 shadow-2xl shadow-zinc-300/20 md:flex-nowrap lg:m-0 lg:flex lg:w-fit lg:gap-6 lg:space-y-0 lg:border-transparent lg:bg-transparent lg:p-0 lg:shadow-none dark:shadow-none dark:lg:bg-transparent z-30 ${
                        menuState ? 'block' : 'hidden'
                    } lg:hidden`}>
                            <div className="lg:hidden w-full flex justify-center">
                                <NavigationMenu>
                                    <NavigationMenuList className="flex-col items-center space-y-3">
                                        {menuItems.map((item) => (
                                            <NavigationMenuItem key={item.id}>
                                                <NavigationMenuLink
                                                    asChild
                                                    className="text-foreground hover:text-accent-foreground block font-bold text-xl uppercase duration-150">
                                                    <Link 
                                                        href={item.href}
                                                        onClick={() => setMenuState(false)}
                                                        className={`transition-all duration-300 ease-in-out hover:scale-[1.02] hover:bg-transparent focus:bg-transparent focus:outline-none focus-visible:ring-0 ${pathname.includes(item.href.substring(1)) ? 'text-primary' : ''}`}
                                                    >
                                                        {t(item.translationKey)}
                                                    </Link>
                                                </NavigationMenuLink>
                                            </NavigationMenuItem>
                                        ))}
                                    </NavigationMenuList>
                                </NavigationMenu>
                            </div>
                </div>
            </nav>
        </header>
    )
}
